import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Sparkles, PhoneCall, PlayCircle, Send } from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import { useApp, API } from "../AppContext";
import { Spinner } from "./Brand";

/**
 * Wraps a page that requires an active subscription (paid or in 14-day Pro trial).
 * - While loading: shows a spinner.
 * - If no access: shows a professional module-not-included screen with 3 CTAs.
 * - If access granted: renders children.
 */
export default function SubscriptionGate({ children, featureName }) {
  const { lang, user } = useApp();
  const { loading, hasAccess } = useSubscription();
  const nav = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [form, setForm] = useState({ phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const isFr = lang === "fr";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="subscription-gate-loading">
        <Spinner />
      </div>
    );
  }

  if (hasAccess) return children;

  const submitLead = async (kind) => {
    setErr(""); setSending(true);
    try {
      await axios.post(`${API}/sales/lead`, {
        kind,
        name: user?.name || "",
        company: user?.company || "",
        email: user?.email || "",
        phone: form.phone,
        message: form.message || (kind === "demo"
          ? (isFr ? "Je souhaite une démonstration personnalisée." : "I would like a personalized demo.")
          : (isFr ? "Je souhaite être contacté par un conseiller." : "I would like to be contacted by an advisor.")),
        plan_interest: featureName || "PROGÈRE DEVIS AI",
      });
      setSent(true);
      setTimeout(() => { setDemoOpen(false); setContactOpen(false); setSent(false); setForm({ phone: "", message: "" }); }, 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || (isFr ? "Erreur d'envoi" : "Send failed"));
    } finally { setSending(false); }
  };

  const title = isFr
    ? `${featureName || "PROGÈRE DEVIS AI"} n'est pas inclus dans votre abonnement actuel.`
    : `${featureName || "PROGÈRE DEVIS AI"} is not included in your current subscription.`;
  const subtitle = isFr
    ? "Débloquez ce module en choisissant le forfait qui correspond à votre équipe, ou parlez à notre équipe pour une démonstration personnalisée adaptée à votre entreprise."
    : "Unlock this module by choosing the plan that fits your team, or talk to our team for a personalized demo tailored to your company.";

  return (
    <div className="max-w-3xl mx-auto py-10 px-4" data-testid="subscription-gate-locked">
      <div className="glass-strong p-8 sm:p-10 text-center glow-gold">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
          style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)" }}>
          <Lock size={28} color="#D4AF37" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: "#D4AF37" }}>
          {isFr ? "Module PROGÈRE" : "PROGÈRE Module"}
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-black mb-3 tracking-tight" style={{ color: "#F5F5F5" }}>
          {title}
        </h2>
        <p className="mb-8 text-sm sm:text-base max-w-xl mx-auto" style={{ color: "rgba(245,245,245,0.7)" }}>
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button onClick={() => nav("/app/billing")} className="btn-gold" data-testid="gate-cta-subscribe">
            <Sparkles size={14} /> {isFr ? "Voir les forfaits" : "View plans"}
          </button>
          <button onClick={() => setDemoOpen(true)} className="btn-red" data-testid="gate-cta-demo">
            <PlayCircle size={14} /> {isFr ? "Demander une démonstration" : "Request a demo"}
          </button>
          <button onClick={() => setContactOpen(true)} className="btn-ghost text-sm" data-testid="gate-cta-sales">
            <PhoneCall size={14} /> {isFr ? "Contacter les ventes" : "Contact sales"}
          </button>
        </div>
      </div>

      {(demoOpen || contactOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
          onClick={() => { setDemoOpen(false); setContactOpen(false); setSent(false); }}
          data-testid="gate-modal-backdrop">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl p-8"
            style={{
              background: "linear-gradient(180deg, rgba(24,24,24,0.98), rgba(11,11,11,0.98))",
              border: "1px solid rgba(212,175,55,0.35)",
              boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9)",
            }}
            data-testid="gate-modal">
            {sent ? (
              <div className="text-center py-6" data-testid="gate-modal-ok">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.4)" }}>
                  <Send size={24} color="#4ADE80" />
                </div>
                <div className="font-heading text-lg font-bold mb-1" style={{ color: "#F5F5F5" }}>
                  {isFr ? "Merci ! Nous vous contactons rapidement." : "Thanks! We'll contact you soon."}
                </div>
                <div className="text-sm" style={{ color: "rgba(245,245,245,0.65)" }}>
                  {isFr ? "Notre équipe vous répond sous 24 heures ouvrables." : "Our team replies within 24 business hours."}
                </div>
              </div>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "#D4AF37" }}>
                  {demoOpen ? (isFr ? "Démonstration personnalisée" : "Personalized demo") : (isFr ? "Contact ventes" : "Sales contact")}
                </div>
                <div className="font-heading text-xl font-black mb-4" style={{ color: "#F5F5F5" }}>
                  {isFr ? "Parlons de votre projet" : "Let's talk about your project"}
                </div>
                <div className="space-y-3">
                  <div className="text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>
                    <span style={{ color: "rgba(245,245,245,0.85)", fontWeight: 600 }}>{user?.name}</span> · {user?.email} · {user?.company}
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "rgba(245,245,245,0.7)" }}>
                      {isFr ? "TÉLÉPHONE (facultatif)" : "PHONE (optional)"}
                    </label>
                    <input type="tel" className="input-dark mt-2" placeholder="(514) 555-0123"
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="gate-phone" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: "rgba(245,245,245,0.7)" }}>
                      {isFr ? "MESSAGE" : "MESSAGE"}
                    </label>
                    <textarea className="input-dark mt-2" rows={4}
                      placeholder={isFr ? "Décrivez brièvement vos besoins..." : "Briefly describe your needs..."}
                      value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="gate-message" />
                  </div>
                  {err && <div className="text-xs px-2 py-1.5 rounded" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B" }}>{err}</div>}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => submitLead(demoOpen ? "demo" : "contact")} disabled={sending} className="btn-gold flex-1 disabled:opacity-50" data-testid="gate-modal-submit">
                      {sending ? <Spinner /> : <><Send size={14} /> {isFr ? "Envoyer" : "Send"}</>}
                    </button>
                    <button onClick={() => { setDemoOpen(false); setContactOpen(false); }} className="btn-ghost" data-testid="gate-modal-cancel">
                      {isFr ? "Annuler" : "Cancel"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
